import React, { useState } from 'react'
import styled from '@emotion/styled'
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Card, Popconfirm, Select, Space } from 'antd'
import {
  PortalArticleSection,
  PortalArticleSectionContentItem,
} from '../../../../models/PortalArtigoModel'
import { cloneContentItem, createDefaultContentItem } from '../../helpers/block-defaults'
import { PORTAL_SECTION_CONTENT_TYPE_LABELS } from '../../helpers/portal-editor-options'
import { PortalNoteStyleEditor } from '../PortalNoteStyleEditor'
import { RichTextField, RichTextListEditor } from '../PortalRichTextFields'
import { PortalCardsBlockEditor } from './PortalCardsBlockEditor'
import { ImageBlockEditor } from './ImageBlockEditor'
import { PortalTableBlockEditor } from './PortalTableBlockEditor'
import { PortalYoutubeBlockEditor } from './PortalYoutubeBlockEditor'

interface PortalSectionContentEditorProps {
  value: PortalArticleSection
  isIntro?: boolean
  onChange: (section: PortalArticleSection) => void
}

export function PortalSectionContentEditor({
  value,
  isIntro,
  onChange,
}: PortalSectionContentEditorProps) {
  const content = getEditableSectionContent(value, isIntro)
  const [autoEditTarget, setAutoEditTarget] = useState<{ index: number; token: number } | null>(
    null
  )

  const commitContent = (nextContent: PortalArticleSectionContentItem[]) => {
    onChange({
      ...value,
      content: nextContent,
    })
  }

  const updateItem = (index: number, item: PortalArticleSectionContentItem) => {
    commitContent(
      content.map((currentItem, currentIndex) => (currentIndex === index ? item : currentItem))
    )
  }

  const addItem = (type: PortalArticleSectionContentItem['type']) => {
    const nextContent = [...content, createDefaultContentItem(type)]
    commitContent(nextContent)

    if (type !== 'cards') {
      setAutoEditTarget((currentTarget) => ({
        index: nextContent.length - 1,
        token: (currentTarget?.token || 0) + 1,
      }))
    }
  }

  const removeItem = (index: number) => {
    commitContent(content.filter((_, currentIndex) => currentIndex !== index))
  }

  const duplicateItem = (index: number) => {
    const nextContent = [...content]
    nextContent.splice(index + 1, 0, cloneContentItem(content[index]))
    commitContent(nextContent)
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= content.length) return

    const nextContent = [...content]
    const [item] = nextContent.splice(index, 1)
    nextContent.splice(targetIndex, 0, item)
    commitContent(nextContent)
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {content.map((item, index) => (
        <Card
          key={`${item.type}-${item.id || index}`}
          size="small"
          title={`${index + 1}. ${PORTAL_SECTION_CONTENT_TYPE_LABELS[item.type]}`}
          extra={
            <Space wrap>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => moveItem(index, -1)}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === content.length - 1}
                onClick={() => moveItem(index, 1)}
              />
              <Button size="small" icon={<CopyOutlined />} onClick={() => duplicateItem(index)}>
                Duplicar
              </Button>
              <Popconfirm
                title={`Remover ${PORTAL_SECTION_CONTENT_TYPE_LABELS[item.type].toLowerCase()}`}
                description="Tem certeza que deseja remover este item?"
                okText="Remover"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => removeItem(index)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          }
        >
          <SectionContentItemEditor
            value={item}
            autoEditToken={autoEditTarget?.index === index ? autoEditTarget.token : undefined}
            onAutoEditHandled={() => setAutoEditTarget(null)}
            onChange={(nextItem) => updateItem(index, nextItem)}
          />
        </Card>
      ))}

      <AddContentActions>
        <Button icon={<PlusOutlined />} onClick={() => addItem('paragraph')}>
          Parágrafo
        </Button>
        {!isIntro && (
          <>
            <Button icon={<PlusOutlined />} onClick={() => addItem('bullets')}>
              Marcadores
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => addItem('note')}>
              Nota
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => addItem('cards')}>
              Cards
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => addItem('table')}>
              Tabela
            </Button>
          </>
        )}
        <Button icon={<PlusOutlined />} onClick={() => addItem('image')}>
          Imagem
        </Button>
        <Button icon={<PlusOutlined />} onClick={() => addItem('youtube')}>
          YouTube
        </Button>
      </AddContentActions>
    </Space>
  )
}

function SectionContentItemEditor({
  value,
  autoEditToken,
  onAutoEditHandled,
  onChange,
}: {
  value: PortalArticleSectionContentItem
  autoEditToken?: number
  onAutoEditHandled?: () => void
  onChange: (item: PortalArticleSectionContentItem) => void
}) {
  if (value.type === 'paragraph') {
    return (
      <RichTextField
        label="Parágrafo"
        value={value.content}
        autoOpenToken={autoEditToken}
        onAutoOpenHandled={onAutoEditHandled}
        onChange={(content) => onChange({ ...value, content: content || '' })}
      />
    )
  }

  if (value.type === 'bullets') {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <RichTextListEditor
          label="Marcadores"
          addLabel="Adicionar marcador"
          value={value.items || []}
          autoOpenToken={autoEditToken}
          autoOpenIndex={0}
          onAutoOpenHandled={onAutoEditHandled}
          onChange={(items) => onChange({ ...value, items })}
        />
        <Select
          value={value.listStyle || 'disc'}
          style={{ width: 180 }}
          onChange={(listStyle) => onChange({ ...value, listStyle })}
          options={[
            { value: 'disc', label: 'Marcadores' },
            { value: 'decimal', label: 'Numerada' },
            { value: 'none', label: 'Sem marcador' },
          ]}
        />
      </Space>
    )
  }

  if (value.type === 'note') {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <RichTextField
          label="Nota"
          value={value.content}
          autoOpenToken={autoEditToken}
          onAutoOpenHandled={onAutoEditHandled}
          onChange={(content) => onChange({ ...value, content: content || '' })}
        />
        <PortalNoteStyleEditor
          value={value.noteStyle}
          onChange={(noteStyle) => onChange({ ...value, noteStyle })}
        />
      </Space>
    )
  }

  if (value.type === 'image') {
    return (
      <ImageBlockEditor
        value={value.image}
        anchorId={value.anchorId}
        onChange={(image, anchorId) => onChange({ ...value, image, anchorId })}
      />
    )
  }

  if (value.type === 'table') {
    return (
      <PortalTableBlockEditor
        value={value}
        autoEditToken={autoEditToken}
        onAutoEditHandled={onAutoEditHandled}
        onChange={onChange}
      />
    )
  }

  if (value.type === 'youtube') {
    return (
      <PortalYoutubeBlockEditor
        value={value}
        autoEditToken={autoEditToken}
        onAutoEditHandled={onAutoEditHandled}
        onChange={onChange}
      />
    )
  }

  return <PortalCardsBlockEditor value={value} onChange={onChange} />
}

function getEditableSectionContent(section: PortalArticleSection, isIntro?: boolean) {
  const content = section.content || []
  return isIntro
    ? content.filter((item) => ['paragraph', 'image', 'youtube'].includes(item.type))
    : content
}

const AddContentActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`
